import {
  Cdr,
  CdrDetail,
  CdrModel,
} from '../interfaces/missed-call-cdr.interface';
import { CdrCallDirection, CdrCallStatus } from '../enums/cdr.enum';

interface CdrCallLeg {
  targetNumber: string;
  isNoAnswer: boolean;
  isBusy: boolean;
  isAnswered: boolean;
  talkDuration: number;
  ringTimeUtc: Date;
}

function createCdrCallLeg(detail: CdrDetail): CdrCallLeg {
  return {
    targetNumber: detail.targetNumber,
    isNoAnswer: detail.callStatusId === CdrCallStatus.NOANSWER,
    isBusy: detail.callStatusId === CdrCallStatus.BUSY,
    isAnswered: detail.callStatusId === CdrCallStatus.ANSWERED,
    talkDuration: detail.talkDuration,
    ringTimeUtc: new Date(detail.ringTimeDate),
  };
}

export function mapMissedCallCdr(
  cdrResponse: Cdr | null,
  userExtension: string,
  voicemailExtension: string | null,
): CdrModel | null {
  if (
    cdrResponse === null ||
    cdrResponse.directionId !== CdrCallDirection.INBOUND
  ) {
    return null;
  }

  return cdrResponse.callee === userExtension
    ? processDirectMissedCall(cdrResponse, userExtension, voicemailExtension)
    : processQueueMissedCall(cdrResponse, userExtension, voicemailExtension);
}

function extractCallLegs(details: CdrDetail[] | null): CdrCallLeg[] | null {
  if (!details) {
    return null;
  }

  return details
    .sort(
      (detailA: CdrDetail, detailB: CdrDetail) =>
        new Date(detailA.addTimeDate).getTime() -
        new Date(detailB.addTimeDate).getTime(),
    )
    .map((detail: CdrDetail) => createCdrCallLeg(detail));
}

function createCdrModel(
  cdrResponse: Cdr,
  callee: string | null,
  ringTime: Date,
): CdrModel {
  const caller = cdrResponse.caller?.replace('+', '').trim() || null;
  const finalCallee = callee?.replace('+', '').trim() || null;

  return {
    id: String(cdrResponse.cdrId),
    caller,
    directionId: cdrResponse.directionId,
    callee: finalCallee,
    ringTimeDate: ringTime.toISOString(),
    sessionId: String(cdrResponse.sessionId),
  };
}

function isDirectMissedCall(
  legs: CdrCallLeg[],
  userExtension: string,
): boolean {
  if (legs.length === 0) return true;

  return (
    legs.length === 1 &&
    legs[0].targetNumber === userExtension &&
    (legs[0].isNoAnswer || legs[0].isBusy) &&
    legs[0].talkDuration === 0
  );
}

function isQueueMissedCall(legs: CdrCallLeg[]): boolean {
  return (
    legs.length > 1 &&
    legs.slice(1).every((leg: CdrCallLeg) => leg.isNoAnswer || leg.isBusy)
  );
}

function isMissedCallAnsweredByVoicemail(
  legs: CdrCallLeg[],
  voicemailExtension: string | null,
  isMissedCallFunc: (legs: CdrCallLeg[]) => boolean,
): boolean {
  if (legs.length === 0 || voicemailExtension === null) {
    return false;
  }

  const lastLeg = legs[legs.length - 1];
  if (!(lastLeg.targetNumber === voicemailExtension && lastLeg.isAnswered)) {
    return false;
  }

  const precedingLegs = legs.slice(0, -1);
  return isMissedCallFunc(precedingLegs);
}

function processDirectMissedCall(
  cdrResponse: Cdr,
  userExtension: string,
  voicemailExtension: string | null,
): CdrModel | null {
  const cdr = createCdrModel(
    cdrResponse,
    userExtension,
    new Date(cdrResponse.startTimeDate),
  );

  const legs = extractCallLegs(cdrResponse.cdrDetails);
  if (legs === null) {
    return cdr;
  }

  if (
    !isDirectMissedCall(legs, userExtension) &&
    !isMissedCallAnsweredByVoicemail(
      legs,
      voicemailExtension,
      (callLegs: CdrCallLeg[]) => isDirectMissedCall(callLegs, userExtension),
    )
  ) {
    return null;
  }

  return cdr;
}

function processQueueMissedCall(
  cdrResponse: Cdr,
  userExtension: string,
  voicemailExtension: string | null,
): CdrModel | null {
  const legs = extractCallLegs(cdrResponse.cdrDetails);
  if (legs === null) {
    return null;
  }

  const agentLeg = legs.find(
    (leg: CdrCallLeg) =>
      leg.targetNumber === userExtension && (leg.isNoAnswer || leg.isBusy),
  );
  if (!agentLeg) {
    return null;
  }

  if (
    !isQueueMissedCall(legs) &&
    !isMissedCallAnsweredByVoicemail(
      legs,
      voicemailExtension,
      isQueueMissedCall,
    )
  ) {
    return null;
  }

  return createCdrModel(
    cdrResponse,
    agentLeg.targetNumber?.replace('+', '').trim() || null,
    agentLeg.ringTimeUtc,
  );
}

const MissedCallCollapseIntervalSeconds: number = 5;

export function deduplicateCdrsByInterval(
  cdrs: (CdrModel | null)[] | null,
): CdrModel[] {
  if (cdrs === null) {
    return [];
  }

  const validCdrs: CdrModel[] = cdrs.filter(
    (cdr: CdrModel | null): cdr is CdrModel => cdr !== null,
  );

  if (validCdrs.length === 0) {
    return [];
  }

  const grouped: Record<string, CdrModel[]> = validCdrs.reduce(
    (acc: Record<string, CdrModel[]>, cdr: CdrModel) => {
      const key: string = `${cdr.caller}|${cdr.callee}|${cdr.directionId}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(cdr);
      return acc;
    },
    {} as Record<string, CdrModel[]>,
  );

  const result: CdrModel[] = [];

  Object.values(grouped).forEach((group: CdrModel[]) => {
    const ordered: CdrModel[] = group.sort(
      (cdrA: CdrModel, cdrB: CdrModel) =>
        new Date(cdrA.ringTimeDate).getTime() -
        new Date(cdrB.ringTimeDate).getTime(),
    );

    if (ordered.length === 0) return;

    let intervalStart: CdrModel = ordered[0];
    let previous: CdrModel = ordered[0];

    for (let i: number = 1; i < ordered.length; i++) {
      const current: CdrModel = ordered[i];
      const diffSeconds: number =
        (new Date(current.ringTimeDate).getTime() -
          new Date(previous.ringTimeDate).getTime()) /
        1000;

      if (diffSeconds > MissedCallCollapseIntervalSeconds) {
        result.push(intervalStart);
        intervalStart = current;
      }

      previous = current;
    }

    result.push(intervalStart);
  });

  return result.sort(
    (cdrA: CdrModel, cdrB: CdrModel) =>
      new Date(cdrB.ringTimeDate).getTime() -
      new Date(cdrA.ringTimeDate).getTime(),
  );
}
